import { injectable } from 'inversify'
import type { FetchOptions } from 'ofetch'
import queryString from 'qs'

export type BaseHeadersType = Record<string, string>
export type HeaderBuilder = (
  currentHeaders: BaseHeadersType,
  requestOptions: FetchOptions<'json' | 'stream'>,
) => BaseHeadersType | Promise<BaseHeadersType>
export type HeadersBuilders = HeaderBuilder[]

export const methodsHasPayload = (method: string) =>
  new Set(['post', 'put', 'patch']).has(method)

@injectable()
export class RequestBuilder {
  private headersBuilders: HeadersBuilders = []

  async prepareRequest(
    method: string,
    rawBody: unknown,
    rawOptions: FetchOptions<'json' | 'stream'>,
  ) {
    const contentType = this.getContentTypeByPayload(rawBody)
    const payloadIsJson
      = contentType === 'application/json'
        && methodsHasPayload(method)
        && rawBody

    const body = payloadIsJson ? JSON.stringify(rawBody) : rawBody
    const headers = await this.prepareHeaders(rawOptions, contentType)
    const options: FetchOptions<'json' | 'stream'> = this.prepareOptionsQuery({
      ...rawOptions,
      headers,
    })

    return { body, options }
  }

  protected getContentTypeByPayload(payload: unknown) {
    switch (true) {
      case typeof payload === 'string': {
        return 'application/x-www-form-urlencoded'
      }
      case typeof FormData !== 'undefined' && payload instanceof FormData: {
        return undefined
      }
      default: {
        return 'application/json'
      }
    }
  }

  protected prepareOptionsQuery(options: FetchOptions<'json'>) {
    options.query = Object.fromEntries(
      Object.entries(options.query || {}).filter(([key]) => typeof key !== 'symbol'),
    )
    if (
      options.query
      && typeof options.query === 'object'
      && Object.values(options.query).some(v => typeof v === 'object')
    ) {
      options.query = queryString
        .stringify(options.query, { encode: false })
        .split('&')
        .reduce<Record<string, string>>((acc: Record<string, string>, part: string) => {
          const [key, value] = part.split('=')
          acc[key] = value
          return acc
        }, {})
    }
    return options
  }


  registerHeadersBuilder(builder: HeaderBuilder) {
    this.headersBuilders.push(builder)
  }

  protected async prepareHeaders(
    options: FetchOptions<'json' | 'stream'>,
    contentType: string | undefined,
  ): Promise<BaseHeadersType> {
    const defaultHeaders: BaseHeadersType = {
      ...(contentType === 'application/json'
        ? { Accept: 'application/json' }
        : {}),
      ...(contentType ? { 'Content-Type': contentType } : {}),
    }

    const headersChain: HeadersBuilders = [
      ...this.headersBuilders,
      headers => Object.assign(headers, options?.headers || {}),
    ]

    return headersChain.reduce(async (headers, builder) => {
      return builder(await headers, options)
    }, Promise.resolve(defaultHeaders))
  }
}
